<script lang="ts">
	import { inputClasses } from '$lib/InputText.svelte';
	import LoadingButton from '$lib/LoadingButton.svelte';
	import { displayPrice } from '$lib/commonLogic';
	import Dialog from '$lib/components/Dialog.svelte';
	import { Attending } from '$lib/eventTypes';
	import IconCross from '$lib/icons/IconCross.svelte';
	import type { UiEvent } from '$lib/server/supabase';
	import { format } from 'date-fns';
	import type { IJsonSheet } from 'json-as-xlsx';
	import IconDownload from '~icons/ic/outline-file-download';
	import IconEye from '~icons/mdi/eye-outline';
	import * as m from '$lib/paraglide/messages.js';
	import { getLocale } from '$lib/paraglide/runtime';
	import de from 'date-fns/locale/de/index.js'; // need to be imported like this https://github.com/sveltejs/kit/issues/612 | do not import entire "/locale" cuz it blows bundle size

	interface Props {
		event: UiEvent;
		[key: string]: any;
	}

	let { event, ...rest }: Props = $props();

	const columnDateFormat = getLocale() === 'de' ? 'dd.mm.yyyyy HH:MM' : 'MM/dd/yyyy, h:mm AM/PM';
	const dateFormat = 'Pp';
	const dateFormatParams = { locale: getLocale() === 'de' ? de : undefined };

	let showTables = $state(false);
	let spreadsheets = $derived(getSpreadsheets(event));
	let selectedSheet: string = $state(m.eventTable_guests());
	let openSheet: IJsonSheet | undefined = $derived(
		spreadsheets.find((x) => x.sheet === selectedSheet) ?? spreadsheets[0]
	);

	function getSpreadsheets(event: UiEvent): IJsonSheet[] {
		const sheets = [
			{
				sheet: m.eventTable_guests(),
				columns: [
					{ label: m.eventTable_name(), value: m.eventTable_name() },
					{ label: m.eventTable_attendance(), value: m.eventTable_attendance() },
					{ label: m.eventTable_companions(), value: m.eventTable_companions() },
					{ label: m.eventTable_paidAllegedly(), value: m.eventTable_paidAllegedly() },
					{ label: m.eventTable_comment(), value: m.eventTable_comment() },
					...event.questions.map((x) => ({ label: x.text, value: x.text })),
					{ label: m.eventTable_contact(), value: m.eventTable_contact() },
					{ label: m.eventTable_registeredAt(), value: m.eventTable_registeredAt(), format: columnDateFormat },
				],
				content: event.guests
					.filter((x) => x.attending !== Attending.WAITLIST)
					.map((guest) => ({
						[m.eventTable_name()]: guestName(guest.userId),
						[m.eventTable_attendance()]:
							guest.attending === Attending.ATTENDING
								? m.eventTable_accepted()
								: guest.attending === Attending.MAYBE
									? m.eventTable_maybe()
									: m.eventTable_declined(),
						[m.eventTable_companions()]: guest.companions.join(', '),
						[m.eventTable_paidAllegedly()]: guest.paidAllegedly,
						[m.eventTable_comment()]: guest.comment,
						...Object.fromEntries(event.questions.map((q) => [q.text, guest.answers[q.id]])),
						[m.eventTable_contact()]:
							guest.contactPreference === 'email'
								? guest.email
								: guest.contactPreference === 'phone'
									? guest.phone
									: null,
						[m.eventTable_registeredAt()]: format(new Date(guest.createdAt), dateFormat, dateFormatParams),
						insertDate: new Date(guest.createdAt),
					}))
					.sort((a, b) => a.insertDate.getTime() - b.insertDate.getTime())
					.sort((a, b) => -a.Zusage?.localeCompare(b?.Zusage)),
			},
			{
				sheet: m.eventTable_bringingList(),
				columns: [
					{ label: m.eventTable_item(), value: m.eventTable_item() },
					{ label: m.eventTable_guest(), value: m.eventTable_guest() },
					{ label: m.eventTable_attendance(), value: m.eventTable_attendance() },
				],
				content: event.items
					.map((x) => ({
						Name: x.name,
						Gast: guestName(x.userId),
						Zugesagt:
							event.guests.find((y) => y.userId === x.userId)?.attending === Attending.MAYBE
								? m.eventTable_maybe()
								: m.eventTable_accepted(),
					}))
					.sort((a, b) => a.Gast.localeCompare(b.Gast)),
			},
			{
				sheet: m.eventTable_info(),
				columns: [
					{ label: m.eventTable_name(), value: m.eventTable_name() },
					{ label: m.eventTable_address(), value: m.eventTable_address() },
					{ label: m.eventTable_start(), value: m.eventTable_start() },
					{ label: m.eventTable_description(), value: m.eventTable_description() },
					{ label: m.eventTable_entryFee(), value: m.eventTable_entryFee() },
					{ label: m.eventTable_guestsAccepted(), value: m.eventTable_guestsAccepted() },
					{ label: m.eventTable_guestsMaybe(), value: m.eventTable_guestsMaybe() },
					{ label: m.eventTable_guestsDeclined(), value: m.eventTable_guestsDeclined() },
					{ label: m.eventTable_visitors(), value: m.eventTable_visitors() },
				],
				content: [
					{
						Name: event.name,
						Adresse: event.location,
						Beginn: format(new Date(event.beginAt), dateFormat, dateFormatParams),
						Beschreibung: event.description,
						Eintritt: displayPrice(event) || m.eventTable_free(),
						'Gäste zugesagt': event.guests.filter((x) => x.attending === Attending.ATTENDING).length,
						'Gäste vielleicht': event.guests.filter((x) => x.attending === Attending.MAYBE).length,
						'Gäste abgesagt': event.guests.filter((x) => x.attending === Attending.NOT_ATTENDING).length,
						'Besucher auf Link': event.visitors.length,
					},
				],
			},
		];

		if (event.maxGuests) {
			// insert at position 1 into array
			sheets.splice(1, 0, {
				sheet: m.eventTable_waitlist(),
				columns: [
					{ label: m.eventTable_name(), value: 'Name' },
					{ label: m.eventTable_companions(), value: 'Begleitung' },
					{ label: m.eventTable_comment(), value: 'Kommentar' },
					...event.questions.map((x) => ({ label: x.text, value: x.text })),
					{ label: m.eventTable_contact(), value: 'Kontakt' },
					{ label: m.eventTable_registeredAt(), value: 'Eingetragen am', format: columnDateFormat },
				],
				content: event.guests
					.filter((x) => x.attending === Attending.WAITLIST)
					.map((guest) => ({
						Name: guestName(guest.userId),
						Begleitung: guest.companions.join(', '),
						Kommentar: guest.comment,
						...Object.fromEntries(event.questions.map((q) => [q.text, guest.answers[q.id]])),
						Kontakt:
							guest.contactPreference === 'email'
								? guest.email
								: guest.contactPreference === 'phone'
									? guest.phone
									: null,
						'Eingetragen am': format(new Date(guest.createdAt), dateFormat, dateFormatParams),
						insertDate: new Date(guest.createdAt),
					}))
					.sort((a, b) => a?.['Eingetragen am']?.getTime() - b?.['Eingetragen am']?.getTime()),
			});
		}

		return sheets;
	}

	function guestName(userId: string) {
		let name = event.owners.find((x) => x.userId === userId)?.user?.name;
		if (name !== undefined) return name ?? '';
		return event.guests.find((x) => x.user?.id === userId)?.name ?? '';
	}

	async function downloadAsExcelFile() {
		const { default: toXlsx } = await import('json-as-xlsx'); // heavy size, used rarely => only load on demand
		toXlsx(spreadsheets, {
			fileName: event.name.replace(/[^\p{L}\p{N} -_]/gu, '') + ' ' + new Date(), // replace chars that could be problematic in filenames
		});
	}
</script>

<button onclick={() => (showTables = true)} class="btn btn-secondary {rest.class}" type="button">
	<IconEye class="mr-1 inline-block size-6" />
	{m.eventTable_viewTable()}
</button>

{#if showTables}
	<Dialog closeButton={false} bind:open={showTables} class="max-w-dvw max-h-dvh w-full">
		<div class="">
			<header class="flex gap-4 px-4 pb-2 pt-4">
				<select class="{inputClasses} min-w-40" bind:value={selectedSheet}>
					{#each spreadsheets as sheet}
						<option value={sheet.sheet}>{sheet.sheet}</option>
					{/each}
				</select>

				<LoadingButton onClick={downloadAsExcelFile} class="btn btn-secondary">
					<IconDownload class="-mt-1 inline-block" />
					{m.eventTable_download()}
				</LoadingButton>

				<button
					onclick={() => (showTables = false)}
					class="absolute right-[10px] top-[10px] inline-flex
                    appearance-none items-center justify-center rounded-full text-violet-800
                    hover:bg-violet-100 focus:shadow-violet-400"
				>
					<IconCross class="size-8" />
				</button>
			</header>

			<div class="mx-auto flex h-[calc(100vh-5rem)] flex-col px-4">
				{#if openSheet}
					<div class="w-full overflow-x-auto pb-2">
						<table class="min-w-full border-separate border-spacing-0">
							<thead>
								<tr>
									{#each openSheet.columns as column, i}
										<th
											scope="col"
											title={column.label}
											onclick={(e) => {
												e.currentTarget.classList.toggle('truncate');
											}}
											class="sticky top-0 max-w-80 truncate border-b border-gray-200 bg-white py-3.5 pr-6 text-left text-sm font-semibold text-gray-900"
										>
											{column.label}
										</th>
									{/each}
								</tr>
							</thead>
							<tbody class="bg-white">
								{#each openSheet.content as row}
									<tr
										class="even:bg-gray-50"
										onclick={(e) => {
											for (const child of e.currentTarget.children) {
												child.classList.toggle('truncate');
											}
										}}
									>
										{#each openSheet.columns as column, i}
											{@const value = ((row[column.value] ?? '') + '').trim()}
											<td
												class="max-w-80 truncate border-b border-gray-100 py-4 pr-6 text-sm font-medium text-gray-900"
												title={value}
											>
												{#if value}
													{value}
												{:else}
													<span class="italic text-gray-300">–</span>
												{/if}
											</td>
										{/each}
									</tr>
								{/each}
							</tbody>
						</table>
					</div>
				{/if}
			</div>
		</div>
	</Dialog>
{/if}
